import React from 'react';
import type { HorseEmergency } from '../../../../types/horseProfile';

interface EmergencySectionProps {
  emergency: HorseEmergency;
}

const DEFAULT_EMERGENCY: HorseEmergency = {
  primaryVet: {
    name: '',
    phone: '',
    email: ''
  },
  backupVet: {
    name: '',
    phone: '',
    email: ''
  },
  insurance: {
    provider: '',
    policyNumber: '',
    expiryDate: '',
    coverage: ''
  },
  specialNeeds: [],
  allergies: []
};

export const EmergencySection: React.FC<EmergencySectionProps> = ({ emergency = DEFAULT_EMERGENCY }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Tierärztliche Kontakte</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {emergency.primaryVet && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Haupttierarzt</h5>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                <strong>Name:</strong> {emergency.primaryVet.name}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                <strong>Telefon:</strong> {emergency.primaryVet.phone}
              </p>
              {emergency.primaryVet.email && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>E-Mail:</strong> {emergency.primaryVet.email}
                </p>
              )}
            </div>
          )}

          {emergency.backupVet && Object.keys(emergency.backupVet).some(key => emergency.backupVet[key]) && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Vertretungstierarzt</h5>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                <strong>Name:</strong> {emergency.backupVet.name}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                <strong>Telefon:</strong> {emergency.backupVet.phone}
              </p>
              {emergency.backupVet.email && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  <strong>E-Mail:</strong> {emergency.backupVet.email}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {emergency.insurance && Object.keys(emergency.insurance).some(key => emergency.insurance[key]) && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Versicherung</h4>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Versicherer:</strong> {emergency.insurance.provider}
                </p>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  <strong>Policen-Nr.:</strong> {emergency.insurance.policyNumber}
                </p>
              </div>
              <div>
                {emergency.insurance.expiryDate && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Gültig bis:</strong>{' '}
                    {new Date(emergency.insurance.expiryDate).toLocaleDateString('de-DE')}
                  </p>
                )}
                {emergency.insurance.coverage && (
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    <strong>Leistungsumfang:</strong> {emergency.insurance.coverage}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {emergency.specialNeeds?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Besondere Bedürfnisse</h4>
          <ul className="list-disc list-inside space-y-1">
            {emergency.specialNeeds.map((need, index) => (
              <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                {need}
              </li>
            ))}
          </ul>
        </div>
      )}

      {emergency.allergies?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Allergien</h4>
          <div className="flex flex-wrap gap-2">
            {emergency.allergies.map((allergy, index) => (
              <span
                key={index}
                className="px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              >
                {allergy}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};